/**
 * **Disengage — the Full action's rules core.** Pure rules (no I/O, no dice): the single
 * grounded step the Disengage resolution applies. The room (`backend/.../encounterRoom.ts`)
 * calls this; there is no legality gate here (unlike Hide's cover precondition) — RAW attaches
 * no precondition to Disengage, only its budget cost.
 *
 * **Wired on the Hide template, not a bespoke reducer command** — despite the catalog row's
 * `route: "reducer"` display label (`mechActions.ts`/`npcActions.ts`), the actual wiring is a
 * `request_disengage` intent → this step builder → the room folds it with the Full-action
 * spend, mirroring `hideSteps`/`hide.ts` exactly. The landed status is
 * {@link DISENGAGED_STATUS}, which `@lancer-vtt/domain`'s `movementContext` reads to skip
 * installing the engagement mid-move stop predicate.
 *
 * **Expiry, for free.** `expiry: { kind: "turn", edge: "end", who: "target" }` rides the duration bus
 * (`packages/rules/src/encounter/effects/durationBus.ts`) unmodified: a `turn_end` lifecycle
 * event whose `combatant` matches the carrier removes a `target_next_turn_end` instance
 * regardless of whether it was applied on a PRIOR turn or the carrier's own CURRENT turn — so
 * a mid-own-turn Disengage expires at exactly end-of-current-turn, RAW's "until the end of
 * your current turn" wording, with zero new expiry machinery.
 *
 * **DELIBERATELY does not stamp `appliedTurn`.** This is the one site in the codebase where
 * leaving the stamp OFF is the correct call, not an oversight: stamping `appliedTurn` here would
 * make the instance skip its OWN application turn's end (the genuinely-next-turn semantics
 * `tables.ts`/`reactionRegistry.ts` deliberately opt into), which would push Disengage's expiry
 * to the actor's NEXT turn — a real regression against RAW's "current turn" wording. If a future
 * sweep is tempted to "finish the stamp sweep" here too, don't — check the RAW wording first,
 * same as any other site.
 */

import { type EncounterState, type ResolvedStep, DISENGAGED_STATUS, hasStatus } from "@lancer-vtt/domain";

/**
 * The grounded steps a Disengage resolution applies: land {@link DISENGAGED_STATUS} on the
 * actor, `target_next_turn_end` (removed at the end of the actor's own current turn — see this
 * module's doc). Idempotent: an actor already Disengaged yields **no** step (so the room
 * broadcasts nothing and doesn't re-charge) — mirrors `hideSteps`'s idempotent shape.
 */
export function disengageSteps(state: EncounterState, actor: string): ResolvedStep[] {
  const c = state.combatants[actor];
  if (!c || hasStatus(c.statuses, DISENGAGED_STATUS)) return [];
  return [{ op: "status_add", target: actor, status: DISENGAGED_STATUS, expiry: { kind: "turn", edge: "end", who: "target" }, source: actor }];
}
